/**
 * Author: Claude Code
 * Generation: Lobby page with room list and join functionality
 * Version: v3.2.183
 * Modified: 2025-10-27 (Spectate → auto navigate to /game-page + send scene:ready)
 */
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@store/AuthStore';
import { useRoomStore } from '@store/RoomStore';
import { useEffect, useState } from 'react';
import CreateRoomModal from '@components/CreateRoomModal';

export default function LobbyPage() {
  const navigate = useNavigate();
  const { isAuthenticated, openAuthModal, getAndClearRedirect, redirectAfterAuth } = useAuthStore();
  const { rooms, loadRooms, joinRoom, currentRoom, isInRoom } = useRoomStore();
  const { setSinglePlayerMode } = useRoomStore();
  const [soloLoading, setSoloLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadRooms();
  }, [loadRooms]);

  // E2E: 支持通过 ?clear=1 触发浏览器端缓存清理，保证每次测试是干净环境
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('clear') === '1') {
      try {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        localStorage.removeItem('room-store');
        sessionStorage?.clear?.();
        // 可选：清理 Cache Storage（不报错即可）
        if ('caches' in window) {
          caches.keys().then(keys => keys.forEach(k => caches.delete(k))).catch(()=>{});
        }
        console.log('[Lobby] Cleared browser caches for clean E2E run');
      } catch (e) {
        console.warn('[Lobby] Cache clear warn:', (e as Error).message);
      }
    }
  }, []);

  const handleJoinGame = async (roomId: string) => {
    if (!isAuthenticated) {
      openAuthModal('signin');
    } else {
      const success = await joinRoom(roomId);
      if (success) {
        navigate('/create-agent');
      }
    }
  };

  const watchGame = async (roomId: string) => {
    // 必须登录才能观战：未登录则弹出登录框，并记录意图
    if (!isAuthenticated) {
      openAuthModal('signin', `/intent/spectate?roomId=${roomId}`);
      return;
    }
    // URL承载上下文：直接进入 /game/:roomId，由 GamePage 统一负责 spectator:join + handshake + snapshot
    // 禁止写入 last-room-id（仅使用显式路由传递 roomId）
    navigate(`/game/${roomId}`);
  };

  // 登录后自动执行“刚才的意图”（支持 spectate）
  useEffect(() => {
    if (!isAuthenticated) return;
    const redir = redirectAfterAuth;
    if (!redir) return;
    try {
      // 只在 Lobby 处理 spectate 意图，处理后清空
      const url = new URL(redir, window.location.origin);
      if (url.pathname === '/intent/spectate') {
        const roomId = url.searchParams.get('roomId') || '';
        if (roomId) {
          (async () => {
            // 登录后交由 URL 恢复：跳入 /game/:roomId
            // 禁止写入 last-room-id（仅使用显式路由传递 roomId）
            navigate(`/game/${roomId}`);
            try { getAndClearRedirect(); } catch {}
          })();
        } else {
          try { getAndClearRedirect(); } catch {}
        }
      }
    } catch {
      try { getAndClearRedirect(); } catch {}
    }
  }, [isAuthenticated, redirectAfterAuth, navigate, getAndClearRedirect]);

  const handleCreateRoom = () => {
    if (!isAuthenticated) {
      openAuthModal('signin');
      return;
    }
    setShowCreateModal(true);
  };

  const handleSolo = () => {
    if (!isAuthenticated) { openAuthModal('signin'); return; }
    if (soloLoading) return;
    setSoloLoading(true);
    try {
      setSinglePlayerMode(true);
      navigate('/create-agent');
    } finally {
      // 前端路由切换后由 AgentCreationPage 接手，不在此处恢复以避免抖动
      setTimeout(() => setSoloLoading(false), 1500);
    }
  };

  const handleModalClose = () => {
    setShowCreateModal(false);
  };

  const handleRoomCreated = () => {
    setShowCreateModal(false);
    navigate('/create-agent');
  };

  return (
    <div className="main-container">
      <div className="page active">
        <div className="content-card">
          <div className="card-header">
            <h2 className="card-title">Game Lobby</h2>
            <p className="card-description">Choose a room to join or spectate</p>
          </div>
          <div className="card-body">
            {/* v3.2.49: Statistics bar */}
            <div className="lobby-stats">
              <div className="lobby-stat-card">
                <div className="lobby-stat-value">47</div>
                <div className="lobby-stat-label">👁️ Online Viewers</div>
              </div>
              <div className="lobby-stat-card">
                <div className="lobby-stat-value">132</div>
                <div className="lobby-stat-label">🤖 Active Agents</div>
              </div>
              <div className="lobby-stat-card">
                <div className="lobby-stat-value">1,847</div>
                <div className="lobby-stat-label">⚔️ Today's Battles</div>
              </div>
            </div>

            {!isAuthenticated && (
              <div className="lobby-notice">
                <span className="lobby-notice-text">Sign in to play and earn rewards</span>
                <div className="lobby-notice-actions">
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => openAuthModal('signin')}
                  >
                    Sign In
                  </button>
                </div>
              </div>
            )}

            {/* Actions: Create Room / Solo Experience */}
            <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <button
                className="btn btn-primary btn-lg"
                onClick={handleCreateRoom}
                style={{ minWidth: '240px', flex: 1 }}
              >
                Create New Room
              </button>
              <button
                className="btn btn-secondary btn-lg"
                onClick={handleSolo}
                disabled={soloLoading}
                style={{ minWidth: '240px', flex: 1 }}
              >
                Solo Experience (Start Now)
              </button>
            </div>

            <div className="room-grid">
              {rooms.map((room) => (
                <div key={room.id} className="room-card">
                  <div className="room-card-header">
                    <span className="room-id">{room.name || `Room #${room.id.slice(-3)}`}</span>
                    <span className={`room-status ${room.status.toLowerCase()}`}>
                      {room.status === 'WAITING' ? 'Waiting' : room.status === 'ACTIVE' ? 'In Progress' : room.status}
                    </span>
                  </div>
                  <div className="room-stats">
                    <div className="stat-item">👥 {room.participantCount}/{room.maxParticipants} Players</div>
                    <div className="stat-item">⚡ {room.status === 'ACTIVE' ? `${room.participantCount} Alive` : '— Alive'}</div>
                    <div className="stat-item">👁️ {room.spectatorCount || 0} Viewers</div>
                    <div className="stat-item">💎 100 Bet</div>
                  </div>
                  <div className="room-actions">
                    {room.status === 'WAITING' && room.participantCount < room.maxParticipants ? (
                      currentRoom?.id === room.id ? (
                        <button className="btn btn-success btn-md" disabled>
                          ✓ Joined
                        </button>
                      ) : isInRoom ? (
                        <button className="btn btn-secondary btn-md" disabled>
                          In Another Room
                        </button>
                      ) : (
                        <button
                          className="btn btn-primary btn-md"
                          onClick={() => handleJoinGame(room.id)}
                        >
                          Join
                        </button>
                      )
                    ) : (
                      <button className="btn btn-secondary btn-md" disabled>
                        Full
                      </button>
                    )}
                    {/* P2.1: Only enable spectate button for ACTIVE games */}
                    <button
                      className="btn btn-ghost btn-md"
                      onClick={() => watchGame(room.id)}
                      disabled={room.status !== 'ACTIVE'}
                    >
                      {room.status === 'ACTIVE' ? 'Spectate' : 'Spectate'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Create Room Modal */}
      <CreateRoomModal show={showCreateModal} onClose={handleModalClose} />
    </div>
  );
}
